// Fase 17.3, Plano 06 (D-14) — três jeitos de começar o roteiro de uma peça; todos preenchem o MESMO editor de etapas:
// a partir de um modelo, copiando outra peça ou montando etapa por etapa. Nada é gravado aqui: quem salva é a ficha.
import { useEffect, useState } from 'react';
import type { ModeloRoteiroResumoDto, PecaRoteiroListaItemDto, ServicoComCelulasDto } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { CampoSelect } from '../../../components/ui/campo-select';
import { FormDialog, FormDialogBody, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../../components/ui/form-dialog';
import { Input } from '../../../components/ui/input';
import { etapasDaVersao, novaEtapa, type EtapaEdicao } from '../../../lib/roteiros/etapas';
import { buscarFicha, listarModelos, listarPecas } from '../../../lib/roteiros/roteiros-api';

export type ModoInicioRoteiro = 'MODELO' | 'COPIA' | 'ZERO';

export const ROTULO_MODO_MODELO = 'A partir de um modelo';
export const ROTULO_MODO_COPIA = 'Copiar de outra peça';
export const ROTULO_MODO_ZERO = 'Montar etapa por etapa';

/** O que o editor recebe ao escolher um jeito de começar. `modeloId`: id = seguir esse modelo; null = sem modelo. */
export interface InicioDeRoteiro {
  readonly etapas: EtapaEdicao[];
  readonly modeloId: string | null;
}

export interface IniciarRoteiroDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly servicos: readonly ServicoComCelulasDto[];
  readonly onEscolher: (inicio: InicioDeRoteiro) => void;
  /** Peça aberta na ficha: fica fora da lista de cópia. */
  readonly materialIdAtual?: string;
}

const MODOS: readonly { modo: ModoInicioRoteiro; rotulo: string; ajuda: string }[] = [
  { modo: 'MODELO', rotulo: ROTULO_MODO_MODELO, ajuda: 'Usa as etapas de um modelo pronto. A peça passa a seguir o modelo até você mudar alguma etapa.' },
  { modo: 'COPIA', rotulo: ROTULO_MODO_COPIA, ajuda: 'Copia o roteiro vigente de uma peça parecida.' },
  { modo: 'ZERO', rotulo: ROTULO_MODO_ZERO, ajuda: 'Começa com uma etapa em branco e você completa o resto.' },
];

/** Linhas "1. 06 — Pintura · célula X" para a prévia das etapas de um modelo. */
export function resumoDasEtapas(modelo: Pick<ModeloRoteiroResumoDto, 'etapas'>, servicos: readonly ServicoComCelulasDto[]): string[] {
  return etapasDaVersao(modelo.etapas, servicos).map((etapa, indice) => {
    const servico = servicos.find((s) => s.id === etapa.servicoId);
    return `${indice + 1}. ${servico ? `${servico.codigo} — ${servico.nome}` : 'Serviço'} · ${etapa.celulaId || 'sem célula'}`;
  });
}

export function IniciarRoteiroDialog({ aberto, onAbertoChange, servicos, onEscolher, materialIdAtual }: IniciarRoteiroDialogProps) {
  const [modo, setModo] = useState<ModoInicioRoteiro>('MODELO');
  const [modelos, setModelos] = useState<readonly ModeloRoteiroResumoDto[]>([]);
  const [modeloId, setModeloId] = useState('');
  const [busca, setBusca] = useState('');
  const [pecas, setPecas] = useState<readonly PecaRoteiroListaItemDto[] | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto) return;
    setErro(null);
    setPecas(null);
    setBusca('');
    let vivo = true;
    void listarModelos().then((lista) => { if (vivo) setModelos(lista.filter((modelo) => modelo.ativo && modelo.versaoVigente !== null)); })
      .catch(() => { if (vivo) setErro('Não foi possível carregar os modelos. Tente novamente.'); });
    return () => { vivo = false; };
  }, [aberto]);

  const modeloEscolhido = modelos.find((modelo) => modelo.id === modeloId) ?? null;

  const escolher = (inicio: InicioDeRoteiro): void => { onAbertoChange(false); onEscolher(inicio); };

  const buscarPecas = async (): Promise<void> => {
    if (busca.trim() === '') { setErro('Digite parte do código ou do nome da peça.'); return; }
    setOcupado(true);
    setErro(null);
    try {
      const resposta = await listarPecas({ busca: busca.trim(), tamanhoPagina: 20 });
      setPecas(resposta.itens.filter((peca) => peca.versaoVigente !== null && peca.materialId !== materialIdAtual));
    } catch {
      setErro('Não foi possível buscar as peças. Tente novamente.');
    } finally {
      setOcupado(false);
    }
  };

  const copiarDe = async (peca: PecaRoteiroListaItemDto): Promise<void> => {
    setOcupado(true);
    setErro(null);
    try {
      const ficha = await buscarFicha(peca.materialId);
      const vigente = ficha.roteiro?.vigente;
      if (!vigente) { setErro('Esta peça não tem roteiro vigente para copiar.'); return; }
      escolher({ etapas: etapasDaVersao(vigente.etapas, servicos), modeloId: ficha.roteiro?.modelo?.id ?? null });
    } catch {
      setErro('Não foi possível carregar o roteiro da peça. Tente novamente.');
    } finally {
      setOcupado(false);
    }
  };

  const primeiroServico = servicos[0];

  return (
    <FormDialog open={aberto} onOpenChange={(valor) => { if (!ocupado) onAbertoChange(valor); }}>
      <FormDialogContent>
        <FormDialogHeader>
          <FormDialogTitle>Definir o roteiro da peça</FormDialogTitle>
          <FormDialogDescription>Escolha como começar. Depois você ajusta as etapas no editor e salva com o motivo.</FormDialogDescription>
        </FormDialogHeader>
        <FormDialogBody>
          <div role="group" aria-label="Como começar o roteiro" className="flex flex-wrap gap-2">
            {MODOS.map((opcao) => (
              <Button key={opcao.modo} type="button" variant={modo === opcao.modo ? 'default' : 'outline'} aria-pressed={modo === opcao.modo} onClick={() => { setModo(opcao.modo); setErro(null); }}>
                {opcao.rotulo}
              </Button>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">{MODOS.find((opcao) => opcao.modo === modo)?.ajuda}</p>

          {modo === 'MODELO' ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label htmlFor="iniciar-modelo" className="text-sm font-semibold">Modelo</label>
                <CampoSelect
                  id="iniciar-modelo"
                  value={modeloId}
                  placeholder={modelos.length === 0 ? 'Nenhum modelo disponível' : 'Escolha o modelo'}
                  options={modelos.map((modelo) => ({ value: modelo.id, label: `${modelo.nome} (${modelo.codigo})` }))}
                  onValueChange={setModeloId}
                />
              </div>
              {modeloEscolhido ? (
                <ol className="flex flex-col gap-1 rounded-md border p-4 text-sm" aria-label="Etapas do modelo">
                  {resumoDasEtapas(modeloEscolhido, servicos).map((linha) => <li key={linha}>{linha}</li>)}
                </ol>
              ) : null}
            </div>
          ) : null}

          {modo === 'COPIA' ? (
            <form className="flex flex-col gap-4" onSubmit={(evento) => { evento.preventDefault(); void buscarPecas(); }}>
              <div className="flex flex-col gap-1">
                <label htmlFor="iniciar-busca" className="text-sm font-semibold">Buscar peça por código ou nome</label>
                <div className="flex gap-2">
                  <Input id="iniciar-busca" value={busca} onChange={(evento) => setBusca(evento.target.value)} />
                  <Button type="submit" variant="outline" disabled={ocupado}>Buscar</Button>
                </div>
              </div>
              {pecas !== null && pecas.length === 0 ? <p className="text-sm text-muted-foreground">Nenhuma peça com roteiro vigente encontrada.</p> : null}
              {pecas && pecas.length > 0 ? (
                <ul className="flex flex-col gap-2" aria-label="Peças com roteiro">
                  {pecas.map((peca) => (
                    <li key={peca.materialId} className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-4">
                      <span className="text-sm"><span className="font-semibold">{peca.codigo}</span> — {peca.nome} · v{peca.versaoVigente}</span>
                      <Button type="button" size="sm" variant="outline" disabled={ocupado} onClick={() => void copiarDe(peca)}>Copiar esta peça</Button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </form>
          ) : null}

          {erro ? <p role="alert" className="text-sm text-destructive">{erro}</p> : null}
        </FormDialogBody>
        <FormDialogFooter>
          <Button type="button" variant="outline" disabled={ocupado} onClick={() => onAbertoChange(false)}>Cancelar</Button>
          {modo === 'MODELO' ? <Button type="button" disabled={!modeloEscolhido} onClick={() => modeloEscolhido && escolher({ etapas: etapasDaVersao(modeloEscolhido.etapas, servicos), modeloId: modeloEscolhido.id })}>Usar este modelo</Button> : null}
          {modo === 'ZERO' ? <Button type="button" disabled={!primeiroServico} onClick={() => primeiroServico && escolher({ etapas: [novaEtapa(primeiroServico)], modeloId: null })}>Começar com uma etapa</Button> : null}
        </FormDialogFooter>
      </FormDialogContent>
    </FormDialog>
  );
}
